import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  Send,
  CheckCircle,
  CreditCard,
  XCircle,
  AlertTriangle,
  Loader2,
} from "lucide-react";

interface StatusActionsProps {
  orderId: string;
  currentStatus: string;
  totalPrice: number;
  depositAmount: number;
  onStatusChange?: () => void;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, note: string) => void;
  isLoading: boolean;
  suggestedAmount: number;
  title: string;
}

function PaymentModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  suggestedAmount,
  title,
}: PaymentModalProps) {
  const [amount, setAmount] = useState(suggestedAmount);
  const [note, setNote] = useState("");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Částka (Kč)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="input"
              min={0}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Poznámka (volitelné)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input resize-none"
              rows={2}
              placeholder="Např. VS, datum platby..."
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost" disabled={isLoading}>
            Zrušit
          </button>
          <button
            onClick={() => onConfirm(amount, note)}
            className="btn-primary"
            disabled={isLoading || amount <= 0}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            Zaznamenat platbu
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  title: string;
  message: string;
  confirmText: string;
  variant?: "primary" | "danger";
}

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title,
  message,
  confirmText,
  variant = "primary",
}: ConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="flex items-start gap-4">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              variant === "danger"
                ? "bg-error-100 text-error-600"
                : "bg-primary-100 text-primary-600"
            }`}
          >
            {variant === "danger" ? (
              <AlertTriangle className="h-5 w-5" />
            ) : (
              <CheckCircle className="h-5 w-5" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {message}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost" disabled={isLoading}>
            Zrušit
          </button>
          <button
            onClick={onConfirm}
            className={variant === "danger" ? "btn-danger" : "btn-primary"}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StatusActions({
  orderId,
  currentStatus,
  totalPrice,
  depositAmount,
  onStatusChange,
}: StatusActionsProps) {
  const queryClient = useQueryClient();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<{
    action: string;
    newStatus: string;
  } | null>(null);
  const [paymentType, setPaymentType] = useState<"deposit" | "full">("deposit");

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      return api.patch(`/api/admin/orders/${orderId}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setShowConfirmModal(null);
      onStatusChange?.();
    },
  });

  const recordPaymentMutation = useMutation({
    mutationFn: async ({ amount, note }: { amount: number; note: string }) => {
      return api.post(`/api/admin/orders/${orderId}/payments`, { amount, note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setShowPaymentModal(false);
      onStatusChange?.();
    },
  });

  const remainingAmount = totalPrice - depositAmount;
  const suggestedDeposit = depositAmount || Math.round(totalPrice * 0.3); // Use provided or 30%

  const handleStatusChange = (newStatus: string) => {
    updateStatusMutation.mutate(newStatus);
  };

  const handlePayment = (amount: number, note: string) => {
    recordPaymentMutation.mutate({ amount, note });
  };

  const renderActions = () => {
    switch (currentStatus) {
      case "inquiry":
        return (
          <button
            onClick={() =>
              setShowConfirmModal({
                action: "sendQuote",
                newStatus: "quoted",
              })
            }
            className="btn-primary w-full"
          >
            <Send className="h-5 w-5" />
            Odeslat cenovou nabídku
          </button>
        );

      case "quoted":
        return (
          <button
            onClick={() =>
              setShowConfirmModal({
                action: "confirm",
                newStatus: "confirmed",
              })
            }
            className="btn-primary w-full"
          >
            <CheckCircle className="h-5 w-5" />
            Potvrdit objednávku
          </button>
        );

      case "confirmed":
        return (
          <button
            onClick={() => {
              setPaymentType("deposit");
              setShowPaymentModal(true);
            }}
            className="btn-primary w-full"
          >
            <CreditCard className="h-5 w-5" />
            Zaznamenat zálohu
          </button>
        );

      case "deposit_paid":
        return (
          <button
            onClick={() => {
              setPaymentType("full");
              setShowPaymentModal(true);
            }}
            className="btn-primary w-full"
          >
            <CreditCard className="h-5 w-5" />
            Zaznamenat doplatek
          </button>
        );

      case "fully_paid":
        return (
          <button
            onClick={() =>
              setShowConfirmModal({
                action: "complete",
                newStatus: "completed",
              })
            }
            className="btn-primary w-full"
          >
            <CheckCircle className="h-5 w-5" />
            Označit jako dokončeno
          </button>
        );

      default:
        return null;
    }
  };

  const getConfirmModalProps = () => {
    switch (showConfirmModal?.action) {
      case "sendQuote":
        return {
          title: "Odeslat cenovou nabídku",
          message:
            "Zákazníkovi bude odeslán email s cenovou nabídkou. Chcete pokračovat?",
          confirmText: "Odeslat nabídku",
        };
      case "confirm":
        return {
          title: "Potvrdit objednávku",
          message:
            "Objednávka bude potvrzena a zákazníkovi bude odeslán email s pokyny k platbě.",
          confirmText: "Potvrdit",
        };
      case "complete":
        return {
          title: "Označit jako dokončeno",
          message: "Objednávka bude označena jako dokončená.",
          confirmText: "Dokončit",
        };
      case "cancel":
        return {
          title: "Zrušit objednávku",
          message:
            "Opravdu chcete zrušit tuto objednávku? Tato akce je nevratná.",
          confirmText: "Zrušit objednávku",
          variant: "danger" as const,
        };
      default:
        return {
          title: "",
          message: "",
          confirmText: "",
        };
    }
  };

  return (
    <div className="space-y-2">
      {renderActions()}

      {currentStatus !== "cancelled" && currentStatus !== "completed" && (
        <button
          onClick={() =>
            setShowConfirmModal({
              action: "cancel",
              newStatus: "cancelled",
            })
          }
          className="btn-ghost w-full text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
        >
          <XCircle className="h-5 w-5" />
          Zrušit objednávku
        </button>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePayment}
        isLoading={recordPaymentMutation.isPending}
        suggestedAmount={
          paymentType === "deposit" ? suggestedDeposit : remainingAmount
        }
        title={
          paymentType === "deposit" ? "Zaznamenat zálohu" : "Zaznamenat doplatek"
        }
      />

      {/* Confirm Modal */}
      {showConfirmModal && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setShowConfirmModal(null)}
          onConfirm={() => handleStatusChange(showConfirmModal.newStatus)}
          isLoading={updateStatusMutation.isPending}
          {...getConfirmModalProps()}
        />
      )}
    </div>
  );
}
